import {
  ApiCustomer,
  CommercialReleaseData,
  CommercialReleaseDeclarationStatus,
  CommercialReleaseStatus, CommercialReleaseSummary,
  FindCustomers,
  ReleaseToParty,
  TaskMessageStatus,
} from "@portbase/bezoekschip-service-typescriptmodels";
import {ComparatorChain} from "../common/comparator-chain";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import lodash from "lodash";
import {sendQuery} from "../common/utils";

export const MATRANS:string = 'INTERFORESTRTM';

const commercialReleasePhases: ReleasePhase[] = ["declared", "accepted", "rejected", "cancel declared", "cancel accepted", "cancel rejected"];
export const terminals: string[] = ['ECTDELTA', 'EUROMAX', 'APMII', 'APMRTM', 'RWG', 'RST', MATRANS];

export function fixForMatrans(shortName: string): string {
  return shortName === MATRANS ? 'MATRANS' : shortName;
}

export function terminalOrganisationName(terminalCode: string) {
  switch (terminalCode) {
    case 'ECTDELTA':
      return 'Hutchison Ports ECT Delta';
    case 'EUROMAX':
      return 'Hutchison Ports ECT Euromax';
    case 'APMII':
      return 'APM Terminals Maasvlakte II B.V.';
    case 'APMRTM':
      return 'Hutchison Ports Delta II';
    case 'RWG':
      return 'Rotterdam World Gateway';
    case 'RST':
      return 'Rotterdam Shortsea Terminal';
    case MATRANS:
      return 'Matrans Rotterdam Terminal B.V.';
  }
}

export const commercialReleaseStatusComparator: ComparatorChain = new ComparatorChain(
  'releaseData.releaseDeadline', '!releaseStatus.cancelled',
  (a: CommercialReleaseSummary, b: CommercialReleaseSummary): number =>
    commercialReleasePhases.indexOf(getPhase(a)) - commercialReleasePhases.indexOf(getPhase(b)),
  'releaseData.equipmentNumber');

export const commercialReleaseItemComparator: ComparatorChain = commercialReleaseStatusComparator.reverseProperties();

export const commercialReleaseDateComparator: ComparatorChain = new ComparatorChain(
  '-releaseStatus.declarationDate', 'releaseData.releaseDeadline', '!releaseStatus.cancelled',
  (a: CommercialReleaseSummary, b: CommercialReleaseSummary): number =>
    commercialReleasePhases.indexOf(getPhase(a)) - commercialReleasePhases.indexOf(getPhase(b)),
  'releaseData.equipmentNumber');

export function initializeCommercialRelease(o: CommercialReleaseSummary = <CommercialReleaseSummary>{}): CommercialReleaseSummary {
  const result = o;
  result.id = o.id;
  result.releaseData = initializeCommercialReleaseData(o.releaseData)
  result.releaseStatus = o.releaseStatus || <CommercialReleaseStatus>{};
  return result;
}

export function initializeCommercialReleaseData(o: CommercialReleaseData = <CommercialReleaseData>{}): CommercialReleaseData {
  return o || <CommercialReleaseData>{};
}

export function getPhase(o: CommercialReleaseSummary): ReleasePhase {
  if (o.releaseStatus.cancelled && (o.releaseStatus.status == "SCHEDULED")) {
    return <ReleasePhase> "cancel accepted";
  }
    return <ReleasePhase>((o.releaseStatus.cancelled ? "cancel " : "") + (!!o.releaseStatus.status ? o.releaseStatus.status.toLowerCase() : 'new'));
}

export type ReleasePhase =
  "scheduled"
  | "declared"
  | "accepted"
  | "rejected"
  | "cancel declared"
  | "cancel accepted"
  | "cancel rejected";

/**
 * Maps the commercial-release-specific status enum onto the shared `TaskMessageStatus` enum so the
 * NEW overview item can render status through the shared `app-status-indicator`/`app-task-message-status-icon`
 * components (same pattern as `NewPortvisitUtils.declarationStatusToTaskMessageStatus`). Used only by
 * `CommercialReleaseOverviewItemComponent` - the OLD item/summary components keep using `getPhase()`.
 */
export function commercialReleaseDeclarationStatusToTaskMessageStatus(status: CommercialReleaseDeclarationStatus): TaskMessageStatus {
  switch (status) {
    case CommercialReleaseDeclarationStatus.SCHEDULED:
      return TaskMessageStatus.SCHEDULED;
    case CommercialReleaseDeclarationStatus.DECLARED:
      return TaskMessageStatus.PENDING;
    case CommercialReleaseDeclarationStatus.ACCEPTED:
      return TaskMessageStatus.ACCEPTED;
    case CommercialReleaseDeclarationStatus.REJECTED:
      return TaskMessageStatus.REJECTED;
    default:
      return TaskMessageStatus.UNKNOWN;
  }
}

/**
 * Shared `FindCustomers` query + flatMap-over-references step for release-to-party lookups, used by
 * both the commercial-release modal's "Parties" tab (`commercial-release-modal-parties.component.ts`)
 * and the new overview's release-to-party facet filter (`commercial-release-overview.component.ts`).
 * Callers that need more than the bare `ReleaseToParty` shape (e.g. the modal's address/zipCode/city
 * enrichment) can read those straight off the returned row's `customerData` - it is carried through
 * unmodified from the raw `ApiCustomer` response rather than re-fetched, so this stays the ONLY place
 * the `FindCustomers` FQN + flatMap logic exists.
 * Results are sorted alphabetically by party name before being returned - both call sites render this
 * list as a plain dropdown of names with no other ranking signal shown to the user (unlike, say, a
 * relevance-scored search-result list with visible scores), and the raw `FindCustomers` response has
 * no documented relevance ordering to preserve. This mirrors the same alphabetical-by-formatted-value
 * sort the sidebar checkbox facet dropdown already applies (`FacetFilterComponent.setFacetValues()`,
 * `lodash.sortBy(this.facetValues, f => f.formattedValue || f.value)`).
 */
export function findReleaseToParties(term: string, ownerShortName: string): Observable<(ReleaseToParty & { customerData?: any })[]> {
  return sendQuery("com.portbase.bezoekschip.common.api.commercialrelease.FindCustomers",
    <FindCustomers>{term: term, ownerShortName: ownerShortName})
    .pipe(map((results: ApiCustomer[]) => lodash.sortBy(lodash.flatMap(results, r => lodash.values(r.references).map(customerRef =>
      <ReleaseToParty & { customerData?: any }>{
        name: r.customerData?.fullName,
        portbaseId: r.portbaseId,
        customerReference: customerRef.reference,
        customerData: r.customerData
      })), party => party.name)));
}
